import React, { useState, useEffect } from "react";
import styled from "styled-components";

import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
    PULL_EMPLOYEE,
    SG_UPDATE_EMPLOYEE_AUDIT,
    SG_EDIT_EMPLOYEE_CATEGORY,
    SG_EDIT_EMPLOYEE_PERMISSION,
  } from "constants/actions";

import AddPermissionModal from "./AddPermissionModal";

const TableContainer = styled.div`
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #333;
  margin: 0;
`;

const ExportButton = styled.button`
  background: #1976d2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: #1565c0;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderCell = styled.th`
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #e9ecef;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f8f9fa;
  }
  
  &:hover {
    background: #e3f2fd;
  }
`;

const TableCell = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #e9ecef;
  color: #333;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

const PermissionTag = styled.span`
  display: inline-block;
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 4px;
  margin: 2px;
  font-size: 12px;
`;

const DebugInfo = styled.div`
  background: #f0f0f0;
  padding: 10px;
  margin: 10px 0;
  font-family: monospace;
  font-size: 12px;
  border-radius: 4px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const EditButton = styled.button`
  background: #476dfa;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  font-family: "Poppins";
  
  &:hover {
    background: #3d5cfa;
  }
`;

const RevokeButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  font-family: "Poppins";
  
  &:hover {
    background: #c82333;
  }
`;

const ConfirmationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ConfirmationContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ConfirmationTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #dc3545;
  margin: 0 0 16px 0;
`;

const ConfirmationText = styled.div`
  font-family: "Poppins";
  font-size: 14px;
  color: #333;
  margin-bottom: 24px;
  line-height: 1.5;
  
  ul {
    margin: 10px 0;
    padding-left: 20px;
  }
  
  li {
    margin: 4px 0;
  }
`;

const ConfirmationActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CancelConfirmButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  font-family: "Poppins";
  
  &:hover {
    background: #5a6268;
  }
`;

const ConfirmRevokeButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  font-family: "Poppins";
  
  &:hover {
    background: #c82333;
  }
`;

const AccessAudit = () => {
    const dispatch = useDispatch();
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showAddPermissionModal, setShowAddPermissionModal] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
    const [employeeToRevoke, setEmployeeToRevoke] = useState(null);

    const {
        get_employee,
        get_employee_category,
        get_auth,
      } = useSelector(
        (state) => ({
          get_employee: state.employees,
          get_employee_category: state.employee_category.employee_org_category,
          get_auth: state.auth,
        }),
        shallowEqual
      );

      // Debug effect to see what's in the store
      useEffect(() => {
        console.log("Store updated:", get_employee);
      }, [get_employee]);

      // Initial load to get employee list and filter
      useEffect(() => {
        if (get_employee?.employees && get_employee.employees.length > 0) {
          const filtered = get_employee.employees.filter(employee => 
            employee.access_data_analytics === 1 && 
            employee.enable_login === true && 
            employee.status_text === "Active"
          );
          
          console.log("Filtered employees:", filtered);
          setFilteredEmployees(filtered);
          setCurrentProcessingIndex(0);
          setIsProcessing(false);
        }
      }, [get_employee?.employees]);

      // Start processing employees sequentially
      useEffect(() => {
        if (filteredEmployees.length > 0 && !isProcessing && currentProcessingIndex < filteredEmployees.length) {
          const currentEmployee = filteredEmployees[currentProcessingIndex];
          
          // Check if we already have this employee's detailed data
          if (!hasDetailedData(currentEmployee.id)) {
            console.log(`Processing employee ${currentProcessingIndex + 1}/${filteredEmployees.length}: ${currentEmployee.email}`);
            setIsProcessing(true);
            getEmployeeRecord(currentEmployee.id);
          } else {
            // If we already have the data, move to next employee
            console.log(`Employee ${currentEmployee.id} already has data, moving to next`);
            setCurrentProcessingIndex(prev => prev + 1);
          }
        }
      }, [filteredEmployees, currentProcessingIndex, isProcessing]);

      // Watch for when current employee's data loads, then move to next
      useEffect(() => {
        if (isProcessing && filteredEmployees.length > 0 && currentProcessingIndex < filteredEmployees.length) {
          const currentEmployee = filteredEmployees[currentProcessingIndex];
          const employeeData = get_employee[currentEmployee.id];
          
          console.log(`Checking employee ${currentEmployee.id} data:`, employeeData);
          console.log(`Has category data:`, !!employeeData?.employee_employee_category);
          console.log(`Has permission data:`, !!employeeData?.employee_employee_permission);
          
          if (hasDetailedData(currentEmployee.id)) {
            console.log(`Employee ${currentEmployee.id} data loaded, moving to next`);
            setIsProcessing(false);
            setCurrentProcessingIndex(prev => prev + 1);
          } else if (employeeData && Object.keys(employeeData).length > 0) {
            // Employee data exists but might not have the expected structure
            console.log(`Employee ${currentEmployee.id} has some data but not expected structure:`, Object.keys(employeeData));
            console.log(`Skipping to next employee...`);
            setIsProcessing(false);
            setCurrentProcessingIndex(prev => prev + 1);
          }
        }
      }, [get_employee, isProcessing, filteredEmployees, currentProcessingIndex]);

      // Add timeout to prevent getting stuck
      useEffect(() => {
        if (isProcessing) {
          const timeout = setTimeout(() => {
            console.log(`Timeout reached for employee ${currentProcessingIndex + 1}, moving to next`);
            setIsProcessing(false);
            setCurrentProcessingIndex(prev => prev + 1);
          }, 5000); // 5 second timeout

          return () => clearTimeout(timeout);
        }
      }, [isProcessing, currentProcessingIndex]);

      const getEmployeeRecord = (id) => {
        console.log("Dispatching PULL_EMPLOYEE for ID:", id);
        dispatch({
            type: PULL_EMPLOYEE,
            payload: {
              id: id,
            },
          });
      };

      const getPrimaryCategory = (employeeData) => {
        const category = employeeData?.employee_employee_category?.category;
        if (!category) return "N/A";
        
        const primaryCategories = category.filter(cat => cat.priority === "primary");
        return primaryCategories.length > 0 
          ? primaryCategories.map(cat => cat.value).join(", ")
          : "N/A";
      };

      const getPermissionDetails = (employeeData) => {
        const permissions = employeeData?.employee_employee_permission?.details;
        if (!permissions || !Array.isArray(permissions)) return [];
        
        return permissions.map(detail => ({
          name: detail.name,
          text: detail.text,
          value: detail.value
        }));
      };

      const getEmployeeDetailData = (employeeId) => {
        return get_employee[employeeId];
      };

      const hasDetailedData = (employeeId) => {
        const data = get_employee[employeeId];
        return data && (data.employee_employee_category || data.employee_employee_permission);
      };

      const getLoadedEmployeesCount = () => {
        return filteredEmployees.filter(emp => hasDetailedData(emp.id)).length;
      };

      // Handle edit employee permissions
      const handleEditEmployee = (employee) => {
        setEditingEmployee(employee);
        setShowAddPermissionModal(true);
      };

      // Handle revoke permissions confirmation
      const handleRevokePermissions = (employee) => {
        setEmployeeToRevoke(employee);
        setShowRevokeConfirm(true);
      };

      // Confirm revoke permissions
      const confirmRevokePermissions = async () => {
        if (!employeeToRevoke) return;

        try {
          // Disable login and data access
          dispatch({
            type: SG_UPDATE_EMPLOYEE_AUDIT,
            payload: {
              id: employeeToRevoke.id,
              enable_login: false,
              access_data_analytics: 0,
              access_subfactor_data: 0,
            },
          });

          // Clear employee categories
          const existingCategory = get_employee_category?.find(
            (c) => c.employee_id === employeeToRevoke.id
          );
          
          if (existingCategory) {
            dispatch({
              type: SG_EDIT_EMPLOYEE_CATEGORY,
              payload: {
                id: existingCategory.id,
                category: [], // Clear all categories
              },
            });
          }

          // Clear detailed permissions
          const employeeData = getEmployeeDetailData(employeeToRevoke.id);
          const existingPermission = employeeData?.employee_employee_permission;
          
          if (existingPermission) {
            dispatch({
              type: SG_EDIT_EMPLOYEE_PERMISSION,
              payload: {
                body: {
                  id: existingPermission.id,
                  details: [], // Clear all detailed permissions
                },
                query: `organization=${get_auth?.organization_id}`,
              },
            });
          }

          console.log(`Revoked all permissions for ${employeeToRevoke.email}`);
        } catch (error) {
          console.error("Error revoking permissions:", error);
        } finally {
          setShowRevokeConfirm(false);
          setEmployeeToRevoke(null);
        }
      };

      // Close modals
      const handleCloseAddModal = () => {
        setShowAddPermissionModal(false);
        setEditingEmployee(null);
      };

  return (
    <TableContainer>
      <HeaderContainer>
        <Title>Employee Access Audit</Title>
        <div style={{ display: 'flex', gap: '10px' }}>
          <ExportButton onClick={() => setShowAddPermissionModal(true)}>
            Add Permissions
          </ExportButton>
          <ExportButton onClick={() => {
           const csvContent = [
             ["Email", "Full Name", "Primary Access", "Group Permissions"],
             ...filteredEmployees
               .filter(emp => hasDetailedData(emp.id))
               .map(emp => {
                 const employeeData = getEmployeeDetailData(emp.id);
                 const primaryAccess = getPrimaryCategory(employeeData);
                 const permissions = getPermissionDetails(employeeData);
                 return [
                   emp.email,
                   employeeData?.employee_full_name || `${emp.first_name} ${emp.last_name}`.trim() || "N/A",
                   primaryAccess,
                   permissions.length > 0 ? permissions.map(p => `${p.name}: ${p.text}`).join(", ") : "Full"
                 ];
               })
           ];
          const csvString = csvContent.map(row => row.join(",")).join("\n");
          const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" });
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "access_audit_filtered_employees.csv";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
                 }} disabled={getLoadedEmployeesCount() === 0}>
          Export to CSV
        </ExportButton>
        </div>
      </HeaderContainer>
      
      <DebugInfo>
        Total employees in store: {get_employee?.employees?.length || 0}<br/>
        Filtered employees: {filteredEmployees.length}<br/>
        Currently processing: {currentProcessingIndex < filteredEmployees.length ? `${currentProcessingIndex + 1}/${filteredEmployees.length}` : 'Complete'}<br/>
        Processing status: {isProcessing ? 'Loading...' : 'Ready'}<br/>
        Employees with detailed data: {getLoadedEmployeesCount()}
      </DebugInfo>
      
      {filteredEmployees.length === 0 ? (
        <LoadingText>Loading employee list...</LoadingText>
      ) : (
        <Table>
          <TableHeader>
            <tr>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Full Name</TableHeaderCell>
              <TableHeaderCell>Primary Access</TableHeaderCell>
              <TableHeaderCell>Group Permissions</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </tr>
          </TableHeader>
                    <TableBody>
            {filteredEmployees
              .filter(employee => hasDetailedData(employee.id))
              .map((employee, index) => {
                const employeeData = getEmployeeDetailData(employee.id);
                
                return (
                  <TableRow key={employee.id}>
                    <TableCell>{employee.email}</TableCell>
                    <TableCell>
                      {employeeData?.employee_full_name || 
                       `${employee.first_name} ${employee.last_name}`.trim() || "N/A"}
                    </TableCell>
                    <TableCell>
                      {getPrimaryCategory(employeeData)}
                    </TableCell>
                    <TableCell>
                      {getPermissionDetails(employeeData).length > 0 ?
                       getPermissionDetails(employeeData).map((perm, index) => (
                         <PermissionTag key={index} title={perm.value}>
                           {perm.name}: {perm.text}
                         </PermissionTag>
                       )) : "Full"}
                    </TableCell>
                    <TableCell>
                      <ActionButtons>
                        <EditButton onClick={() => handleEditEmployee(employee)}>
                          Edit
                        </EditButton>
                        <RevokeButton onClick={() => handleRevokePermissions(employee)}>
                          Revoke
                        </RevokeButton>
                      </ActionButtons>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      )}
      
      <div style={{ marginTop: '20px', color: '#666', fontSize: '14px' }}>
        Showing {filteredEmployees.length} employees with data analytics access and login enabled
      </div>

      <AddPermissionModal 
        isOpen={showAddPermissionModal}
        onClose={handleCloseAddModal}
        editingEmployee={editingEmployee}
      />

      {/* Revoke Confirmation Modal */}
      {showRevokeConfirm && (
        <ConfirmationModal>
          <ConfirmationContent>
            <ConfirmationTitle>Revoke All Permissions</ConfirmationTitle>
            <ConfirmationText>
              Are you sure you want to revoke all permissions for <strong>{employeeToRevoke?.email}</strong>?
              <br />
              This will:
              <ul>
                <li>Disable login access</li>
                <li>Remove data analytics access</li>
                <li>Clear all primary categories</li>
                <li>Remove all detailed permissions</li>
              </ul>
            </ConfirmationText>
            <ConfirmationActions>
              <CancelConfirmButton onClick={() => setShowRevokeConfirm(false)}>
                Cancel
              </CancelConfirmButton>
              <ConfirmRevokeButton onClick={confirmRevokePermissions}>
                Revoke All Permissions
              </ConfirmRevokeButton>
            </ConfirmationActions>
          </ConfirmationContent>
        </ConfirmationModal>
      )}
    </TableContainer>
  );
};

export default AccessAudit;